/* eslint-disable @typescript-eslint/no-empty-function */
import React, { Component, lazy, Suspense } from 'react';
import { NAME_TOOL_HEIGHT } from '@src/js/constant';
import SlideWrapper from '@editor/src/page/index/components/canvas/slide-wrapper';
import ErrorBoundary from '@com/error-boundary';
import RegionWrapper from '@com/region-wrapper';
import styles from './index.less';
import PopupWrapper, { PopupMenuType } from '@com-pc/popup-wrapper';
import { RULER_LENGTH } from '@editor/src/js/constants';
import { CommonColor } from '@editor/src/js/constants/common-color';
import { RefName, DOMId } from '@editor/src/page/index/components/canvas/canvas-constants';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import i18n from '@i18next/i18n';
import closeIcon from '@tencent/docs-design-resources/pc/svg/sidebar_close.svg';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import TMallRecommendation from '@com-pc/template-recommendation';
import { scheduleAfterFirstScreenDisplay } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import {
    IPcCanvasProps,
    IPcCanvasService,
    IPcCanvasState,
} from '@src/slide/application/service/canvas-component/core/pc/type';
import ReactView from '@src/slide/infrastructure/module/independent/view-service-define/react/react-view';
import PcCanvasModel from '@src/slide/application/service/canvas-component/core/pc/pc-canvas-model';
import {
    IPcCanvasServiceDelegates,
} from '@src/slide/application/service/canvas-component/core/pc/pc-canvas-service.interface';
import AsyncBoundary from '@src/framework/frame/components/async-boundary';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import LazyBoundary from '@src/framework/frame/components/LazyBoundary';
import { AiAssistantModal } from '@editor/src/page/index/components/canvas/ai-assistant-modal';

const AsyncChartEditWrapper = lazy(() => scheduleAfterFirstScreenDisplay(() => import('./chart-edit-wrapper')));
const NoteAreaWrapper = lazy(() => import(/* webpackChunkName: "lazyComponents" */ '@com-pc/note-area-wrapper'));
const InsertElementWrapper = lazy(() => import(/* webpackChunkName: "lazyComponents" */ './insert-element-wrapper'));
// const ThemeColorDialog = lazy(() => import(/* webpackChunkName: "lazyComponents" */ '@com-pc/theme-color-dialog'));
export default class Canvas extends Component<IPcCanvasProps, IPcCanvasState> {
    private canvasService: IPcCanvasService;

    // refs
    private container;
    private nameInput;
    private noteArea;

    public constructor(props, context) {
        super(props, context);
        this.canvasService = props.canvasService;
        this.canvasService.setViewDelegate(new class extends ReactView<IPcCanvasProps> {
            public getElementById(id: string) {
                const canvasComponent = this.realView as Canvas;
                return canvasComponent.getRefByName(id);
            }
        }(this));
    }

    public componentDidMount() {
        this.canvasService.componentDidMount();
    }

    public componentWillUnmount() {
        this.canvasService.componentWillUnmount();
    }

    public getRefByName(name: string) {
        switch (name) {
            case RefName.NAME_INPUT:
                return this.nameInput;
            case RefName.CONTAINER:
                return this.container;
            case RefName.NOTE_AREA:
                return this.noteArea;
        }
    }

    // eslint-disable-next-line complexity
    public render() {
        const { canvasSize, disableScrollX, disableScrollY,
            isMasterEditType, curCanvasWrapperStyle } = this.getState() || { };
        const { noteAreaHeight } = this.canvasService.getNoteAreaInfo();
        // 获取的Service理论不执行业务逻辑，只是用来透传给子组件
        const {
            textRulerManager, chartManager,
            guidesManager, gridLineManager, themeColorManager, watermarkManager,
        } = this.canvasService?.getServices() as IPcCanvasServiceDelegates;
        let textRulerRef;
        const { slideWidth = 0, slideHeight = 0 } = this.canvasService?.getPresentationSize() || { };
        const {
            width = 0, height = 0, left = 0, top = 0, scale = 1,
        } = canvasSize || { };
        const overflowXClass = !disableScrollX ? styles['canvas-overflow-x'] : styles['canvas-overflow-hidden-x'];
        const overflowYClass = !disableScrollY ? styles['canvas-overflow-y'] : styles['canvas-scroll-hidden-y'];
        // 之前代码中canvas-scroll绑定了滚动条样式CSS，仅用做样式生效使用
        const scrollClass = (!disableScrollX || !disableScrollY) ? styles['canvas-scroll'] : styles['canvas-no-scroll'];
        const hasOverScrollY = !disableScrollY ? ' hasOverScrollY' : '';
        const rulerOffsetLength = textRulerManager?.displayRuler
            ? RULER_LENGTH : 0;

        return (
            <div
                id={DOMId.CANVAS_WRAPPER}
                className={`${styles.canvasWrapper} ${DOMId.CANVAS_WRAPPER}`}
                style={{
                    cursor: this.canvasService.isDraggingElement() ? 'move' : 'auto',
                    overflow: 'hidden',
                    position: 'relative',
                    borderLeft: `1px solid ${curCanvasWrapperStyle.borderColor}`,
                }}
            >
                { this.renderSlideNameTool() }
                <div className={`${styles.canvas} ${scrollClass} ${overflowXClass} ${overflowYClass} ${hasOverScrollY}`}
                    style={{
                        position: 'absolute',
                        top: isMasterEditType ? 44 : 0,
                        bottom: (1 + noteAreaHeight),
                        left: 0,
                        right: 0,
                    }}
                    id={DOMId.CANVAS}
                    ref={(container) => {
                        this.container = container;
                    }}
                    onMouseDown={this.handleMouseDown}
                    onScrollCapture={this.handleScrollEvent}
                    onDoubleClick={this.handleDoubleClick}
                >
                    {
                        textRulerManager?.displayRuler && <ErrorBoundary>
                            <Suspense fallback={<div></div>}>
                                {
                                    textRulerManager.getLazyComponent({
                                        setRef: (ref) => {
                                            textRulerRef = ref;
                                        },
                                        textRulerManager,
                                        scale,
                                        containerWidth: width,
                                        containerHeight: height,
                                        slideWidth,
                                        slideHeight,
                                        slideLeft: left,
                                        slideTop: top,
                                    })
                                }
                            </Suspense>
                        </ErrorBoundary>
                    }
                    <RegionWrapper region={RegionType.EDITOR} style={{
                        width: `calc(100% - ${rulerOffsetLength}px)`,
                        height: `calc(100% - ${rulerOffsetLength}px)`,
                        position: 'absolute',
                        top: `${rulerOffsetLength}px`,
                        left: `${rulerOffsetLength}px`,
                    }}>
                        <PopupWrapper type={PopupMenuType.ELEMENT} style={{
                            width, height, position: 'absolute', overflow: 'hidden',
                        }}>
                            <SlideWrapper
                                canvasSize={canvasSize}
                                getPageScale={this.getPageScale}
                                updateComponentWhenCanvasDidUpdate={() => {
                                    textRulerRef?.updateComponentWhenIsEditing();
                                }
                                }
                            />
                            <ErrorBoundary>
                                <Suspense fallback={<div></div>}>
                                    <InsertElementWrapper scale={scale} />
                                </Suspense>
                            </ErrorBoundary>
                        </PopupWrapper>
                        {
                            guidesManager && gridLineManager?.getGridLineComponent({
                                scale, slideWidth, slideHeight, width, height, left, top,
                            })
                        }
                    </RegionWrapper>
                </div>
                <div id={DOMId.MULTI_SELECTED_DASH_OUTER}
                    style={{
                        position: 'absolute',
                        transformOrigin: 'top left',
                        transform: `scale(${scale})`,
                        top: isMasterEditType ? top + rulerOffsetLength + NAME_TOOL_HEIGHT : top + rulerOffsetLength,
                        left: left + rulerOffsetLength,
                    }}
                />
                <LazyBoundary renderAfterPromise={this.getAppServices().viewableServiceLayer.noteAreaService.promise()}>
                    <NoteAreaWrapper
                        style={{ display: isMasterEditType ? 'none' : 'block' }}
                        getNoteAreaService={this.getNoteAreaSerivice}
                        ref={(ref) => {
                            this.noteArea = ref;
                        }}
                    />
                </LazyBoundary>
                {
                    chartManager
                    && <Suspense fallback={<div />}>
                        <AsyncChartEditWrapper
                            chartManager={chartManager}
                        />
                    </Suspense>
                }
                {this.canvasService.shouldShowTemplateRecommendation() && (
                    // 本地编辑禁用模版推荐功能，此异化逻辑后面再重构
                    <ErrorBoundary>
                        <Suspense fallback={<div></div>}>
                            <TMallRecommendation />
                        </Suspense>
                    </ErrorBoundary>
                )}
                {watermarkManager
                && <AsyncBoundary>
                    {async () => watermarkManager.promise()
                        .then(watermarkManager => watermarkManager.getLazyWatermarkMask())}
                </AsyncBoundary>}
                <ErrorBoundary>
                    <Suspense fallback={<div></div>}>
                        {/* todo: service化 */}
                        {
                            themeColorManager?.getLazyComponent({
                                slideWidth,
                                slideHeight,
                                rightMenuManager: this.getAppServices().viewableServiceLayer.rightMenuManager,
                                menuManager: this.getAppServices().editableServiceLayer?.menuManager,
                                uiDataService: this.getAppServices().getUiDataService(),
                                tipsManager: this.getAppServices().viewableServiceLayer.tipsManager,
                                userPrivilegeService: this.getAppServices().viewableServiceLayer.userPriviledgeService,
                            })
                        }
                    </Suspense>
                </ErrorBoundary>
            </div>
        );
    }

    private renderSlideNameTool() {
        const {
            isMasterEditType,
        } = this.getState() || { };
        const curSlide = this.canvasService?.getCurrentSlide();
        if (!curSlide || !isMasterEditType) return;
        const getNameLabel = () => ({
            [UIModelType.SLIDE]: i18n.t('幻灯片名称'),
            [UIModelType.MASTER]: i18n.t('母版名称'),
            [UIModelType.LAYOUT]: i18n.t('版式名称'),
        }[curSlide.type]);
        return (
            <div className={styles.slideName} style={{ display: isMasterEditType ? 'block' : 'none', height: NAME_TOOL_HEIGHT }}>
                <div className={styles.name}>
                    <span className={styles.label}>{`${getNameLabel()}：`}</span>
                    <input
                        ref={input => this.nameInput = input}
                        type="text"
                        maxLength={255}
                        onFocus={this.handlerSlideNameFocus}
                        onInput={this.handlerSlideNameChange}
                        onKeyDown={this.onKeydownEnter}/>
                </div>
                <div className={styles.exitMasterMode} onClick={this.exitMasterLayoutEdiType}>
                    <img src={closeIcon} /><span>{i18n.t('关闭母版')}</span>
                </div>
            </div>
        );
    }

    private getState() {
        const state = this.canvasService?.getModel();
        return state as PcCanvasModel;
    }

    private handleMouseDown = (e) => {
        this.canvasService.handleMouseDown(e);
    };

    private handleDoubleClick = (e) => {
        this.canvasService.handleDoubleClick(e);
    };

    private handleScrollEvent = (e) => {
        this.canvasService.handleScrollEvent(e);
    };

    private getPageScale = () => this.canvasService.getPageScale();

    private handlerSlideNameFocus = (e) => {
        this.canvasService.handlerSlideNameFocus(e);
    };

    private handlerSlideNameChange = (e) => {
        this.canvasService.handlerSlideNameChange(e);
    };

    private onKeydownEnter = (e) => {
        this.canvasService.onKeydownEnter(e);
    };

    private exitMasterLayoutEdiType = (e) => {
        this.canvasService.exitMasterLayoutEdiType(e);
    };

    private getNoteAreaSerivice = () => this.getAppServices().viewableServiceLayer.noteAreaService.dangerousInstance!;

    private getAppServices(): IPcEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices;
    }
}
